import { useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const enrollmentSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  course_interest: z.string().min(1, 'Please select a course'),
  preferred_mode: z.string().min(1, 'Please select a learning mode'),
});

export default function Enroll() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(enrollmentSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enrollments`, data);
      toast.success('Enrollment submitted successfully! We will contact you soon.');
      reset();
    } catch (error) {
      console.error('Error submitting enrollment:', error);
      toast.error('Failed to submit enrollment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-brand-dark mb-6">Enroll Now</h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              Take the first step towards academic excellence. Fill out the form below and we'll get in touch with you.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="feature-card">
            <form onSubmit={handleSubmit(onSubmit)} data-testid="enrollment-form" className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-brand-dark font-medium">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="enroll-name-input"
                  {...register('name')}
                  placeholder="Enter your full name"
                  className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email" className="text-brand-dark font-medium">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="enroll-email-input"
                  {...register('email')}
                  placeholder="your.email@example.com"
                  className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="text-brand-dark font-medium">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="enroll-phone-input"
                  {...register('phone')}
                  placeholder="+91 1234567890"
                  className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="course_interest" className="text-brand-dark font-medium">Course Interest *</Label>
                <Select onValueChange={(value) => setValue('course_interest', value)}>
                  <SelectTrigger data-testid="enroll-course-select" className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-brand-blue">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Engineering Mathematics">Engineering Mathematics</SelectItem>
                    <SelectItem value="Engineering Physics">Engineering Physics</SelectItem>
                    <SelectItem value="Computer Science">Computer Science</SelectItem>
                    <SelectItem value="Electronics Engineering">Electronics Engineering</SelectItem>
                    <SelectItem value="Electrical Engineering">Electrical Engineering</SelectItem>
                    <SelectItem value="Instrumentation Engineering">Instrumentation Engineering</SelectItem>
                    <SelectItem value="GATE Preparation - CS">GATE Preparation - CS</SelectItem>
                    <SelectItem value="GATE Preparation - EC">GATE Preparation - EC</SelectItem>
                    <SelectItem value="GATE Preparation - EE">GATE Preparation - EE</SelectItem>
                    <SelectItem value="GATE Preparation - IN">GATE Preparation - IN</SelectItem>
                    <SelectItem value="Custom Course">Custom Course</SelectItem>
                  </SelectContent>
                </Select>
                {errors.course_interest && (
                  <p className="text-red-500 text-sm mt-1">{errors.course_interest.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="preferred_mode" className="text-brand-dark font-medium">Preferred Learning Mode *</Label>
                <Select onValueChange={(value) => setValue('preferred_mode', value)}>
                  <SelectTrigger data-testid="enroll-mode-select" className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-brand-blue">
                    <SelectValue placeholder="Select learning mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1:1 Tutoring">1:1 Tutoring</SelectItem>
                    <SelectItem value="Group Classes">Group Classes</SelectItem>
                    <SelectItem value="Both">Both (1:1 & Group)</SelectItem>
                  </SelectContent>
                </Select>
                {errors.preferred_mode && (
                  <p className="text-red-500 text-sm mt-1">{errors.preferred_mode.message}</p>
                )}
              </div>

              <Button
                type="submit"
                data-testid="enroll-submit-btn"
                disabled={isSubmitting}
                className="w-full btn-primary"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Enrollment'
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-brand-dark mb-8 text-center">What Happens Next?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-brand-blue text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">1</div>
              <h3 className="font-semibold text-brand-dark mb-2">We Review Your Request</h3>
              <p className="text-sm text-slate-600">Our team reviews your enrollment details</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brand-blue text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">2</div>
              <h3 className="font-semibold text-brand-dark mb-2">We Contact You</h3>
              <p className="text-sm text-slate-600">You'll receive a call/email within 24 hours</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brand-blue text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">3</div>
              <h3 className="font-semibold text-brand-dark mb-2">Start Learning</h3>
              <p className="text-sm text-slate-600">Begin your journey to excellence</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
